/** Minimum grace after auth loading completes before redirecting unauthenticated users. */
export const AUTH_RESOLVE_DELAY_MS = 200;

/** Extended grace while Supabase restores a persisted session or processes OAuth hash. */
export const AUTH_RESOLVE_EXTENDED_DELAY_MS = 600;

/** True when OAuth callback hash fragments are still in the URL. */
export function isOAuthCallbackInProgress(): boolean {
  if (globalThis.window === undefined) return false;
  const { hash } = globalThis.window.location;
  return hash.includes("access_token") || hash.includes("refresh_token");
}

/** True when Supabase auth tokens exist in localStorage (session restore likely in flight). */
export function hasPersistedSupabaseSession(): boolean {
  if (globalThis.window === undefined) return false;
  try {
    const storage = globalThis.localStorage;
    if (!storage || typeof storage.key !== "function") {
      return false;
    }
    // Storage is not a plain object — Object.keys(localStorage) does not list item keys.
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (!key) continue;
      if (
        (key.startsWith("sb-") && key.includes("auth-token")) ||
        key.startsWith("supabase.auth.")
      ) {
        return true;
      }
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Delay before treating auth as resolved for redirect decisions.
 * Authenticated users resolve immediately; persisted/OAuth sessions get extra time.
 */
export function getAuthResolveDelayMs(hasUser: boolean): number {
  if (hasUser) return 0;
  if (isOAuthCallbackInProgress()) return AUTH_RESOLVE_EXTENDED_DELAY_MS;
  if (hasPersistedSupabaseSession()) return AUTH_RESOLVE_EXTENDED_DELAY_MS;
  return AUTH_RESOLVE_DELAY_MS;
}

/** Block unauthenticated redirects while session restore or OAuth processing may still complete. */
export function shouldDeferUnauthenticatedRedirect(
  hasUser: boolean,
  authResolved: boolean
): boolean {
  if (hasUser || authResolved) return false;
  return isOAuthCallbackInProgress() || hasPersistedSupabaseSession();
}
